import { type VariantProps, cva } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import type { CommonProps } from '../../types';
import { ButtonOrLink, type ButtonOrLinkProps } from '../button-or-link';
import { tv } from 'tailwind-variants';

export interface TextLinkProps
    extends CommonProps,
        ButtonOrLinkProps,
        VariantProps<typeof TextLinkStyles> {
    icon?: ReactNode;
    enableDrag?: boolean;
    iconAlign?: 'left' | 'right';
}

export const TextLinkStyles = tv({
    base: 'focus hover:underline hover:underline-offset-2 inline-flex items-center',
    variants: {
        intent: {
            primary: 'text-brand-500 hover:text-brand-700 selection:text-white',
            secondary: 'text-white',
        },
        size: {
            lg: 'text-lg leading-6',
            md: 'text-base leading-6',
            sm: 'text-sm leading-5',
            none: null,
        },
        weight: {
            inherit: null,
            light: 'font-light',
        },
        underline: {
            true: 'underline underline-offset-2',
        },
        withIcon: {
            true: 'inline-flex',
            false: '',
        },
    },
    defaultVariants: {
        intent: 'secondary',
        size: 'md',
        underline: true,
        weight: 'light',
    },
});

export const TextLink = ({
    intent,
    size,
    underline,
    icon,
    iconAlign = icon ? 'left' : undefined,
    weight,
    className,
    children,
    testId = 'text-link',
    ...props
}: TextLinkProps): JSX.Element => {
    return (
        <ButtonOrLink
            className={TextLinkStyles({
                intent,
                size,
                underline,
                weight,
                className,
                withIcon: !!icon,
            })}
            testId={testId}
            {...props}
        >
            {icon && iconAlign === 'left' && (
                <span className="mr-2">{icon}</span>
            )}
            {children}
            {icon && iconAlign === 'right' && (
                <span className="ml-2">{icon}</span>
            )}
        </ButtonOrLink>
    );
};
